import { Utils as UtilsModule } from 'common';
import UserCapsEnum from 'common/src/code/utils/UserCaps.enum';
import isNumber from 'lodash/isNumber';
import remove from 'lodash/remove';
import UserActionType from './UserActionType.enum';

const {
	Session,
	UserRole,
	Validation: { isValidEmail, isValidPhone, isValidPhoneWithExt },
	Util: { setBit, hasBitSet, formatPhoneNumber },
} = UtilsModule;

export default class Utils {
	static getRoleUser = (user = {}) => {
		const userCapabilities = user.UserCapabilities || {};
		const capabilities = userCapabilities.Capabilities || 0;

		// Account Owner takes precedence: a user with the ucAccountOwner bit is
		// always reported as Account Owner, even if they also hold a Corp or
		// Region admin role. Falls through to enterprise/Location Admin/User
		// only when the bit is not set.
		if (hasBitSet(capabilities, UserCapsEnum.ucAccountOwner)) {
			return { value: UserRole.AccountOwner, label: 'Account Owner' };
		} else if (user.UserCorporateCapabilities) {
			if (user.UsersCorporationInformation.UserCorpLevel === 1) {
				return { value: UserRole.CorpAdmin, label: 'Corporate Admin' };
			} else if (user.UsersCorporationInformation.UserCorpLevel === 2) {
				return { value: UserRole.RegionAdmin, label: 'Region Admin' };
			} else {
				return { value: '', label: '' };
			}
		} else if (hasBitSet(capabilities, UserCapsEnum.ucManageUsers)) {
			return { value: UserRole.LocationAdmin, label: 'Location Admin' };
		} else {
			return { value: UserRole.SingleUser, label: 'User' };
		}
	};

	static checkIsEnterpriseAdmin = role =>
		role === UserRole.RegionAdmin || role === UserRole.CorpAdmin;

	static getRoleDropDownByUser = (
		action,
		selectedUsers,
		{ canManageAccountOwners } = {}
	) => {
		const role = Session.getUserRole();
		const singleUserDoesNotHaveRegion = selectedUsers.some(
			user => user.Region === ''
		);
		const baseRoles = [
			{
				label: 'Location Admin',
				value: UserRole.LocationAdmin,
			},
			{
				label: 'User',
				value: UserRole.SingleUser,
			},
		];

		// Anyone permitted to manage Account Owners (Account Owner / Corp Admin /
		// Region Admin) can assign the Account Owner role to other users.
		if (canManageAccountOwners) {
			baseRoles.unshift({
				label: 'Account Owner',
				value: UserRole.AccountOwner,
			});
		}

		// Add additional options CorpAdmin role.
		if (role === UserRole.CorpAdmin) {
			baseRoles.unshift(
				{
					label: 'Corporate Admin',
					value: UserRole.CorpAdmin,
				},
				{
					label: 'Region Admin',
					value: UserRole.RegionAdmin,
				}
			);
		}

		// Add Region Admin option for RegionAdmin role.
		if (role === UserRole.RegionAdmin) {
			baseRoles.unshift({
				label: 'Region Admin',
				value: UserRole.RegionAdmin,
			});
		}

		if (action === UserActionType.UpdateUser && singleUserDoesNotHaveRegion) {
			remove(baseRoles, {
				label: 'Region Admin',
				value: UserRole.RegionAdmin,
			});
		}

		// Add 'No Change' option for multi-update case
		if (action === UserActionType.UpdateMultiUser) {
			baseRoles.unshift({
				label: 'No Change',
				value: '',
			});
		}

		return baseRoles;
	};

	static checkConfirmEmail = errMessage => (confirmEmail, state) =>
		state.email === confirmEmail ? '' : errMessage;

	static checkWhiteSpace = errMessage => value =>
		/\s/g.test(value.trim()) ? errMessage : '';

	static checkMin = (minValue, errMessage) => value =>
		value && (value.length >= minValue ? '' : errMessage);

	static checkMax = (maxValue, errMessage) => value =>
		value && (Number(value) > maxValue ? errMessage : '');

	static checkRequired = errMessage => value => (value ? '' : errMessage);

	static checkEmail = errMessage => value =>
		value && (isValidEmail(value) ? '' : errMessage);

	static checkUSPhone = errMessage => value =>
		value &&
		(isValidPhone(value) || isValidPhoneWithExt(value) ? '' : errMessage);

	static checkConfirmPassword = errMessage => (confirmPassword, state) =>
		state.password === confirmPassword ? '' : errMessage;

	static checkContainSpecialCharacter = errMessage => value => {
		const regex = /[-!$%^&*()_+|~=`{}[:;<>?,.@#\]]/g;
		return regex.test(value) ? '' : errMessage;
	};

	static checkPasswordLength = (minLength, maxLength, errMessage) => value =>
		value &&
		(value.length >= minLength && value.length <= maxLength ? '' : errMessage);

	static validateOnField = (formRules, fieldName, formData) => {
		if (!formRules[fieldName]) return {};
		const error =
			formRules[fieldName].rules &&
			formRules[fieldName].rules.reduce((formError, current) => {
				const message = current(formData[fieldName], formData);
				if (message) {
					if (formError[fieldName]) {
						formError[fieldName].push(message);
					} else {
						formError[fieldName] = [message];
					}
				}
				return formError;
			}, {});
		return error;
	};

	static validateData = (formRules, formData) => {
		const { perPrint } = formData;
		let result = {};
		Object.keys(formRules).forEach(fieldName => {
			const error = this.validateOnField(formRules, fieldName, formData);
			result = {
				...result,
				...error,
			};
		});
		if (!perPrint && result.perPrintValue) {
			delete result.perPrintValue;
		}
		return result;
	};

	static getFormDataByAction = (action, selectedUsers, meterNo) => {
		if (action === UserActionType.Add)
			return {
				userName: '',
				firstName: '',
				lastName: '',
				location: isNumber(meterNo) ? meterNo : null, // WEBAPPS-26174, default selected the location on location page
				email: '',
				confirmEmail: '',
				phone: '',
				role: '',
				addFunds: false,
				viewReports: false,
				buySupplies: false,
				costCodesRequired: false,
				editCostCodes: false,
				perPrint: false,
				perPrintValue: 0,
			};
		else if (action === UserActionType.UpdateMultiUser)
			return {
				role: '',
				addFunds: '',
				viewReports: '',
				buySupplies: '',
				costCodesRequired: '',
				editCostCodes: '',
				perPrint: '',
				perPrintValue: 0,
				mask: false,
			};

		const user = selectedUsers[0] || {};
		const {
			UserCapabilities: { Capabilities, PerPrintMaximumAmountLimit },
		} = user;

		const isCheck = (capabilities, fieldName) => {
			const USER_CAPS_MAP_FIELDS = {
				AddFunds: ['ucPurchasePostage'],
				ViewReports: ['ucWebBasedReports', 'ucAdvancedReporting'],
				BuySupplies: ['ucPurchaseFromStore'],
				CostCodesRequired: ['ucReferenceCodeOptional'],
				EditCostCodes: ['ucEditReferenceCodes'],
			};
			if (!USER_CAPS_MAP_FIELDS[fieldName]) return false;
			return USER_CAPS_MAP_FIELDS[fieldName].reduce(
				(acc, cur) => acc && hasBitSet(capabilities, UserCapsEnum[cur]),
				true
			);
		};

		return {
			userName: user.UserName,
			status: user.Status,
			lastName: user.LastName,
			firstName: user.FirstName,
			phone: formatPhoneNumber(user.PhoneNumber),
			email: user.EmailAddress,
			capabilities: Capabilities,
			isUserActivated: user.IsUserActivated,
			perPrint: PerPrintMaximumAmountLimit !== this.MAXIMUM_LIMIT_AMOUNT,
			perPrintValue: PerPrintMaximumAmountLimit,
			addFunds: isCheck(Capabilities, 'AddFunds'),
			viewReports: isCheck(Capabilities, 'ViewReports'),
			buySupplies: isCheck(Capabilities, 'BuySupplies'),
			costCodesRequired: !hasBitSet(
				Capabilities,
				UserCapsEnum.ucReferenceCodeOptional
			),
			editCostCodes: isCheck(Capabilities, 'EditCostCodes'),
			role: this.getRoleUser(user).value,
			location: user.Location,
			region: user.Region,
		};
	};

	// this is the max money value that DB allows (truncating the decimal portion)
	static MAXIMUM_LIMIT_AMOUNT = 922337203685477;

	static getFormRule = (action, userRole) => {
		// All possible rules
		const rules = {
			userName: {
				rules: [
					this.checkRequired('Username must contain at least one letter.'),
					this.checkMin(2, 'Username must be at least two characters.'),
					this.checkWhiteSpace('Username may not contain a space'),
				],
			},
			firstName: {
				rules: [this.checkRequired('First name is required.')],
			},
			lastName: {
				rules: [this.checkRequired('Last name is required.')],
			},
			email: {
				rules: [
					this.checkRequired('Email is required.'),
					this.checkEmail('Invalid Email Address.'),
				],
			},
			confirmEmail: {
				rules: [
					this.checkRequired('Email confirmation is required.'),
					this.checkConfirmEmail('Emails do not match.'),
				],
			},
			phone: {
				rules: [
					this.checkRequired('Phone number is required.'),
					this.checkUSPhone('Invalid Phone Number.'),
				],
			},
			password: {
				rules: [
					this.checkRequired('Password is required.'),
					this.checkPasswordLength(
						14,
						64,
						'Password must be between 15-64 characters in length'
					),
					this.checkContainSpecialCharacter(
						'Password must contain a special character.\n!"#$%&\'()* +,-./:;<=>?@[]^_`{|}~'
					),
				],
			},
			confirmPassword: {
				rules: [
					this.checkRequired('Password confirmation is required.'),
					this.checkPasswordLength(
						14,
						64,
						'Password must be between 15-64 characters in length'
					),
					this.checkContainSpecialCharacter(
						'Password must contain a special character.\n!"#$%&\'()* +,-./:;<=>?@[]^_`{|}~'
					),
					this.checkConfirmPassword('Password confirmation does not match.'),
				],
			},
			role: {
				rules: [this.checkRequired('Role is required.')],
			},
			perPrintValue: {
				rules: [
					this.checkMax(
						this.MAXIMUM_LIMIT_AMOUNT,
						`Print limit cannot exceed $${this.MAXIMUM_LIMIT_AMOUNT}.`
					),
				],
			},
			location: { rules: [this.checkRequired('Location is required.')] },
		};

		// return rules for ADD_NEW_USER
		if (action === UserActionType.Add) {
			const addNewUserRule = {
				firstName: rules.firstName,
				lastName: rules.lastName,
				email: rules.email,
				confirmEmail: rules.confirmEmail,
				phone: rules.phone,
				perPrintValue: rules.perPrintValue,
				role: rules.role,
			};

			if (this.checkIsEnterpriseAdmin(userRole)) {
				addNewUserRule.userName = rules.userName;
				addNewUserRule.location = rules.location;
			}
			return addNewUserRule;

			// return rules for UPDATE_SINGLE_USER
		} else if (action === UserActionType.UpdateUser) {
			return {
				firstName: rules.firstName,
				lastName: rules.lastName,
				email: rules.email,
				phone: rules.phone,
				perPrintValue: rules.perPrintValue,
				role: rules.role,
			};
			// return rules for MULTI-UPDATE
		} else if (action === UserActionType.UpdateMultiUser) {
			return {
				perPrintValue: rules.perPrintValue,
			};
		}

		// return rules for User Activation
		return {
			firstName: rules.firstName,
			lastName: rules.lastName,
			phone: rules.phone,
			password: rules.password,
			confirmPassword: rules.confirmPassword,
		};
	};

	static calculateUserCaps = dataUser => {
		let result = 0;
		// Set for all User Roles
		result = setBit(result, UserCapsEnum.ucClientLogin);
		result = setBit(result, UserCapsEnum.ucFedEx);
		result = setBit(result, UserCapsEnum.ucFedExSubscription);

		// Manual Caps
		if (dataUser.addFunds === true || dataUser.addFunds === 'allowed') {
			result = setBit(result, UserCapsEnum.ucPurchasePostage);
		}
		if (dataUser.buySupplies === true || dataUser.buySupplies === 'allowed') {
			result = setBit(result, UserCapsEnum.ucPurchaseFromStore);
		}
		if (
			dataUser.costCodesRequired === false ||
			dataUser.costCodesRequired === 'No'
		) {
			result = setBit(result, UserCapsEnum.ucReferenceCodeOptional);
		}
		if (
			dataUser.editCostCodes === true ||
			dataUser.editCostCodes === 'allowed'
		) {
			result = setBit(result, UserCapsEnum.ucEditReferenceCodes);
		}
		if (dataUser.viewReports === true || dataUser.viewReports === 'allowed') {
			result = setBit(result, UserCapsEnum.ucWebBasedReports);
			result = setBit(result, UserCapsEnum.ucAdvancedReporting);
		}

		// Role Caps (Account Owner, Location Admin, and enterprise admin roles all get admin caps)
		if (dataUser.role !== UserRole.SingleUser && dataUser.role !== 'User') {
			result = setBit(result, UserCapsEnum.ucManageUsers);
			result = setBit(result, UserCapsEnum.ucChangePhysicalAddress);
			result = setBit(result, UserCapsEnum.ucChangePaymentMethod);
			result = setBit(result, UserCapsEnum.ucChangeServicePlan);
			result = setBit(result, UserCapsEnum.ucChangeContactInfo);
			result = setBit(result, UserCapsEnum.ucChangeShippingAddress);
		}

		// Account Owner role also sets the ucAccountOwner capability bit
		if (dataUser.role === UserRole.AccountOwner) {
			result = setBit(result, UserCapsEnum.ucAccountOwner);
		}

		/**
		 * If Account type is Non-Enterprise and "Role" dropdown = "Location Admin": True
		 */
		const userRole = Session.getUserRole();

		if (
			userRole === UserRole.SingleUser ||
			userRole === UserRole.MultiAdmin ||
			userRole === UserRole.MultiUser
		) {
			if (dataUser.role !== UserRole.SingleUser && dataUser.role !== 'User') {
				result = setBit(
					result,
					UserCapsEnum.ucEditShipmentNotificationCustomization
				);
				result = setBit(result, UserCapsEnum.ucUnused);
			}
		}

		return result;
	};

	static calculateCorpUserCaps = dataUser => {
		let capabilities = 0;
		if (
			dataUser.role === UserRole.CorpAdmin ||
			dataUser.role === UserRole.RegionAdmin
		) {
			capabilities = this.calculateUserCaps(dataUser);
			capabilities = setBit(capabilities, UserCapsEnum.ucUnused);
			capabilities = setBit(
				capabilities,
				UserCapsEnum.ucEditShipmentNotificationCustomization
			);

			if (!hasBitSet(capabilities, UserCapsEnum.ucReferenceCodeOptional)) {
				capabilities = setBit(
					capabilities,
					UserCapsEnum.ucReferenceCodeOptional
				);
			}
		}
		return capabilities;
	};
	/* WEBAPPS-32134, Removed hasCanManageRegions() due to CanManageRegions appcap was never actually implemented in v1
	static hasCanManageRegions = () => {
		const { info = {} } = Session.getAccountInfo();
		const { Capabilities = {} } = info;
		return Boolean(Capabilities.CanManageRegions);
	}; */

	/**
	 * Check if a user is an Account Owner by deriving their role.
	 */
	static isUserAccountOwner = (user = {}) => {
		return this.getRoleUser(user).value === UserRole.AccountOwner;
	};

	static getFieldsChanged = (initData, newData) =>
		Object.keys(newData).filter(key => newData[key] !== initData[key]);

	static disableAllField = users => {
		const userLoginRole = Session.getUserRole();
		const hasCorpAdmin = users.some(({ Role }) => Role === UserRole.CorpAdmin);
		return hasCorpAdmin && userLoginRole === UserRole.RegionAdmin;
	};
}
